import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatLineaLabel } from '../lineaLabel.js';

const REMITO_REGEX = /^\d{5,}$/;
const MAX_RECENT = 8;

const ACTION_ICONS = {
  emitir: '🚚',
  valorizacion: '💲',
  ocr: '🧾',
  guardar: '💾',
  limpiar: '🧹',
};

const STEP_ORDER = ['linea', 'remitente', 'destinatario', 'remitos', 'emitir'];

const normalizeText = (value = '') =>
  String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const includesQuery = (value, query) => normalizeText(value).includes(normalizeText(query));

const resolveClienteLocalidad = cliente =>
  cliente?.localidad?.nombre || cliente?.localidadNombre || cliente?.localidad || '';

const resolveClienteProvincia = cliente =>
  cliente?.provincia?.nombre || cliente?.provinciaNombre || cliente?.provincia || '';

const matchLinea = (linea, query) => {
  if (!query) return true;
  return [linea.codigo, linea.nombre, linea.origen, linea.destino, `${linea.origen} ${linea.destino}`].some(field => includesQuery(field, query));
};

const matchCliente = (cliente, query) => {
  if (!query) return true;
  return [
    cliente.razonSocial,
    cliente.nombreFantasia,
    cliente.cuit,
    cliente.telefono,
    cliente.celular,
    cliente.direccion,
    resolveClienteLocalidad(cliente),
    resolveClienteProvincia(cliente),
  ].some(field => includesQuery(field, query));
};

const buildStatus = state => ({
  linea: Boolean(state.lineaId),
  remitente: Boolean(state.remitenteId),
  destinatario: Boolean(state.destinatarioId),
  remitos: Number(state.remitosCount || 0),
});

const buildMissingErrors = status => {
  const errors = [];
  if (!status.linea) errors.push('Debe seleccionar una línea');
  if (!status.remitente) errors.push('Debe seleccionar remitente');
  if (!status.destinatario) errors.push('Debe seleccionar destinatario');
  if (!status.remitos) errors.push('Agregue al menos 1 remito');
  return errors;
};

export default function useOperatorMode({
  lineas = [],
  clientes = [],
  state,
  actions,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [isGuidedMode, setIsGuidedMode] = useState(true);
  const queryInputRef = useRef(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 180);
    return () => window.clearTimeout(timeoutId);
  }, [query]);

  const status = useMemo(() => buildStatus(state), [state]);

  const currentStep = useMemo(() => {
    if (!status.linea) return 'linea';
    if (!status.remitente) return 'remitente';
    if (!status.destinatario) return 'destinatario';
    if (!status.remitos) return 'remitos';
    return 'emitir';
  }, [status]);

  const rememberRecent = useCallback(item => {
    setRecentItems(prev => [item, ...prev.filter(existing => existing.id !== item.id)].slice(0, MAX_RECENT));
  }, []);

  const actionItems = useMemo(
    () => [
      {
        id: 'action-emitir',
        type: 'action',
        title: 'Emitir guía',
        subtitle: 'Validar formulario y emitir (F5 / Ctrl+Enter)',
        icon: ACTION_ICONS.emitir,
        section: 'Acciones',
        onSelect: async () => {
          console.log('OperatorMode action', 'emitir', {});
          const result = await actions.emitir();
          if (result?.errors?.length) {
            setValidationErrors(result.errors);
            setIsOpen(true);
          } else {
            setValidationErrors([]);
            setIsOpen(false);
          }
        },
      },
      {
        id: 'action-valorizacion',
        type: 'action',
        title: 'Valorización',
        subtitle: 'Abrir modal de valorización',
        icon: ACTION_ICONS.valorizacion,
        section: 'Acciones',
        onSelect: () => {
          console.log('OperatorMode action', 'valorizacion', {});
          actions.valorizacion();
        },
      },
      {
        id: 'action-ocr',
        type: 'action',
        title: 'Leer remito (OCR)',
        subtitle: 'Escanear y completar datos automáticamente',
        icon: ACTION_ICONS.ocr,
        section: 'Acciones',
        onSelect: () => {
          console.log('OperatorMode action', 'ocr', {});
          actions.ocr();
        },
      },
      {
        id: 'action-guardar',
        type: 'action',
        title: 'Guardar borrador',
        subtitle: 'Persistir cambios actuales sin emitir',
        icon: ACTION_ICONS.guardar,
        section: 'Acciones',
        onSelect: () => {
          console.log('OperatorMode action', 'guardar', {});
          actions.guardarBorrador?.();
          setIsOpen(false);
        },
      },
      {
        id: 'action-limpiar',
        type: 'action',
        title: 'Limpiar formulario',
        subtitle: 'Reiniciar campos y remitos cargados',
        icon: ACTION_ICONS.limpiar,
        section: 'Acciones',
        onSelect: () => {
          console.log('OperatorMode action', 'limpiar', {});
          actions.limpiar();
          setIsOpen(false);
        },
      },
    ],
    [actions],
  );

  const remitoItem = useMemo(() => {
    if (!REMITO_REGEX.test(debouncedQuery)) return null;
    return {
      id: `remito-${debouncedQuery}`,
      type: 'remito',
      title: `Agregar remito #${debouncedQuery}`,
      subtitle: 'Presione Enter para agregar y continuar cargando',
      icon: '📦',
      section: 'Remitos',
      onSelect: () => {
        console.log('OperatorMode action', 'remito_add', { numero: debouncedQuery });
        const result = actions.agregarRemitoNumero(debouncedQuery);
        if (result?.ok) {
          setQuery('');
          setValidationErrors([]);
        } else if (result?.error) {
          setValidationErrors([result.error]);
        }
      },
    };
  }, [actions, debouncedQuery]);

  const lineaItems = useMemo(
    () => lineas
      .filter(linea => matchLinea(linea, debouncedQuery))
      .slice(0, 25)
      .map(linea => ({
        id: `linea-${linea.id}`,
        type: 'linea',
        title: formatLineaLabel(linea),
        subtitle: linea.descripcion || linea.nombre || 'Línea',
        icon: '🛣️',
        section: 'Línea',
        onSelect: () => {
          console.log('OperatorMode action', 'linea', { lineaId: linea.id });
          actions.seleccionarLinea(linea);
        },
      })),
    [actions, debouncedQuery, lineas],
  );

  const clienteItems = useMemo(() => {
    const section = currentStep === 'destinatario' ? 'Destinatario' : 'Remitente';
    const type = currentStep === 'destinatario' ? 'cliente_destinatario' : 'cliente_remitente';
    return clientes
      .filter(cliente => matchCliente(cliente, debouncedQuery))
      .slice(0, 35)
      .map(cliente => ({
        id: `${type}-${cliente.id}`,
        type,
        title: cliente.razonSocial || cliente.nombreFantasia || `Cliente ${cliente.id}`,
        subtitle: `${resolveClienteLocalidad(cliente) || 'Sin localidad'} - ${resolveClienteProvincia(cliente) || 'Sin provincia'} - CUIT ${cliente.cuit || 'N/D'}`,
        icon: currentStep === 'destinatario' ? '📥' : '📤',
        section,
        onSelect: () => {
          console.log('OperatorMode action', type, { clienteId: cliente.id });
          if (currentStep === 'destinatario') {
            actions.seleccionarDestinatario(cliente);
          } else {
            actions.seleccionarRemitente(cliente);
          }
        },
      }));
  }, [actions, clientes, currentStep, debouncedQuery]);

  const historyItems = useMemo(
    () => recentItems.map(item => ({ ...item, section: 'Historial reciente' })),
    [recentItems],
  );

  const orderedItems = useMemo(() => {
    const bySection = {
      Acciones: actionItems,
      Línea: lineaItems,
      Remitente: currentStep === 'remitente' ? clienteItems : [],
      Destinatario: currentStep === 'destinatario' ? clienteItems : [],
      Remitos: remitoItem ? [remitoItem] : [],
      'Historial reciente': historyItems,
    };

    if (!isGuidedMode) {
      bySection.Remitente = clientes
        .filter(cliente => matchCliente(cliente, debouncedQuery))
        .slice(0, 20)
        .map(cliente => ({
          id: `cliente-remitente-${cliente.id}`,
          type: 'cliente_remitente',
          title: cliente.razonSocial || cliente.nombreFantasia || `Cliente ${cliente.id}`,
          subtitle: `${resolveClienteLocalidad(cliente) || 'Sin localidad'} - ${resolveClienteProvincia(cliente) || 'Sin provincia'} - CUIT ${cliente.cuit || 'N/D'}`,
          icon: '📤',
          section: 'Remitente',
          onSelect: () => actions.seleccionarRemitente(cliente),
        }));
      bySection.Destinatario = clientes
        .filter(cliente => matchCliente(cliente, debouncedQuery))
        .slice(0, 20)
        .map(cliente => ({
          id: `cliente-destinatario-${cliente.id}`,
          type: 'cliente_destinatario',
          title: cliente.razonSocial || cliente.nombreFantasia || `Cliente ${cliente.id}`,
          subtitle: `${resolveClienteLocalidad(cliente) || 'Sin localidad'} - ${resolveClienteProvincia(cliente) || 'Sin provincia'} - CUIT ${cliente.cuit || 'N/D'}`,
          icon: '📥',
          section: 'Destinatario',
          onSelect: () => actions.seleccionarDestinatario(cliente),
        }));
    }

    const sectionOrder = ['Acciones', 'Línea', 'Remitente', 'Destinatario', 'Remitos', 'Historial reciente'];
    return sectionOrder.flatMap(sectionName => bySection[sectionName] || []);
  }, [actionItems, clienteItems, clientes, currentStep, debouncedQuery, historyItems, isGuidedMode, lineaItems, remitoItem, actions]);

  useEffect(() => {
    if (selectedIndex > orderedItems.length - 1) {
      setSelectedIndex(0);
    }
  }, [orderedItems, selectedIndex]);

  const selectItem = useCallback(async item => {
    if (!item) return;
    rememberRecent({
      id: item.id,
      type: item.type,
      title: item.title,
      subtitle: item.subtitle,
      icon: item.icon,
      onSelect: item.onSelect,
    });
    await item.onSelect();
    if (item.type !== 'remito') {
      setQuery('');
    }
    if (isGuidedMode && item.type !== 'action' && currentStep !== 'remitos') {
      const nextStep = STEP_ORDER[STEP_ORDER.indexOf(currentStep) + 1];
      if (nextStep === 'emitir') {
        setValidationErrors(buildMissingErrors(buildStatus(state)));
      }
    }
  }, [currentStep, isGuidedMode, rememberRecent, state]);

  const onArrow = useCallback(direction => {
    if (!orderedItems.length) return;
    setSelectedIndex(prev => {
      const delta = direction === 'down' ? 1 : -1;
      const next = prev + delta;
      if (next < 0) return orderedItems.length - 1;
      if (next >= orderedItems.length) return 0;
      return next;
    });
  }, [orderedItems.length]);

  const openPalette = useCallback(() => {
    setIsOpen(true);
    setValidationErrors([]);
    window.setTimeout(() => queryInputRef.current?.focus(), 0);
  }, []);

  const closePalette = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);

  const handleKeyDown = useCallback((event, { allowEmitShortcut = false } = {}) => {
    if (event.altKey && event.key === 'F2') {
      event.preventDefault();
      setIsOpen(prev => !prev);
      return true;
    }

    if (allowEmitShortcut && (event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key === 'Enter'))) {
      return false;
    }

    if (!isOpen) {
      return false;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closePalette();
      return true;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onArrow('down');
      return true;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      onArrow('up');
      return true;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      selectItem(orderedItems[selectedIndex]);
      return true;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      const nextStep = STEP_ORDER[STEP_ORDER.indexOf(currentStep) + 1] || STEP_ORDER[0];
      if (nextStep === 'emitir') {
        setSelectedIndex(0);
      }
      return true;
    }

    return false;
  }, [closePalette, currentStep, isOpen, onArrow, orderedItems, selectItem, selectedIndex]);

  const groupedResults = useMemo(() => {
    const groups = [];
    orderedItems.forEach(item => {
      const last = groups[groups.length - 1];
      if (!last || last.title !== item.section) {
        groups.push({ title: item.section, items: [item] });
      } else {
        last.items.push(item);
      }
    });
    return groups;
  }, [orderedItems]);

  return {
    isOpen,
    query,
    setQuery,
    selectedIndex,
    setSelectedIndex,
    groupedResults,
    results: orderedItems,
    currentStep,
    status,
    validationErrors,
    isGuidedMode,
    setIsGuidedMode,
    queryInputRef,
    openPalette,
    closePalette,
    handleKeyDown,
    selectItem,
  };
}
